// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/ai-speakable-text.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Text as the assistant's voice should say it (docs/AI_CHATBOT_PLAN.md, phase 5).
 * The speech models misread digits (Chatterbox said "1960" as "mil o sesenta
 * inta" in Spanish), so numbers are spelled out in the reply's language first,
 * years the way people say them; markdown and links are dropped.
 */
import type { AiChatLanguage } from './ai-languages';

// --- Numbers to words, 0 … 999 999 999 -----------------------------------------

const EN_ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function englishBelow1000(n: number): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (hundreds) parts.push(`${EN_ONES[hundreds]} hundred`);
  if (rest || !hundreds) parts.push(rest < 20 ? EN_ONES[rest]! : EN_TENS[Math.floor(rest / 10)]! + (rest % 10 ? `-${EN_ONES[rest % 10]}` : ''));
  return parts.join(' ');
}

function englishNumber(n: number): string {
  if (n < 1000) return englishBelow1000(n);
  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const parts: string[] = [];
  if (millions) parts.push(`${englishBelow1000(millions)} million`);
  if (thousands) parts.push(`${englishBelow1000(thousands)} thousand`);
  if (rest) parts.push(englishBelow1000(rest));
  return parts.join(' ');
}

/** 1960 → "nineteen sixty", 1905 → "nineteen oh five", 2008 → "two thousand eight", 2024 → "twenty twenty-four". */
function englishYear(n: number): string {
  if (n >= 2000 && n < 2010) return englishNumber(n);
  const high = Math.floor(n / 100);
  const low = n % 100;
  if (low === 0) return `${englishBelow1000(high)} hundred`;
  return `${englishBelow1000(high)} ${low < 10 ? `oh ${EN_ONES[low]}` : englishBelow1000(low)}`;
}

const ES_UNITS = ['cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve', 'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve',
  'veinte', 'veintiuno', 'veintidós', 'veintitrés', 'veinticuatro', 'veinticinco', 'veintiséis', 'veintisiete', 'veintiocho', 'veintinueve'];
const ES_TENS = ['', '', '', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const ES_HUNDREDS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

function spanishBelow1000(n: number, beforeNoun = false): string {
  if (n === 100) return 'cien';
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (hundreds) parts.push(ES_HUNDREDS[hundreds]!);
  if (rest || !hundreds) {
    let words = rest < 30 ? ES_UNITS[rest]! : ES_TENS[Math.floor(rest / 10)]! + (rest % 10 ? ` y ${ES_UNITS[rest % 10]}` : '');
    // "veintiún mil", "treinta y un millones": uno shortens before a noun.
    if (beforeNoun) words = words.replace(/veintiuno$/, 'veintiún').replace(/uno$/, 'un');
    parts.push(words);
  }
  return parts.join(' ');
}

function spanishNumber(n: number): string {
  if (n < 1000) return spanishBelow1000(n);
  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const parts: string[] = [];
  if (millions) parts.push(millions === 1 ? 'un millón' : `${spanishBelow1000(millions, true)} millones`);
  if (thousands) parts.push(thousands === 1 ? 'mil' : `${spanishBelow1000(thousands, true)} mil`);
  if (rest) parts.push(spanishBelow1000(rest));
  return parts.join(' ');
}

const CA_UNITS = ['zero', 'u', 'dos', 'tres', 'quatre', 'cinc', 'sis', 'set', 'vuit', 'nou', 'deu', 'onze', 'dotze', 'tretze', 'catorze', 'quinze', 'setze', 'disset', 'divuit', 'dinou'];
const CA_TENS = ['', '', 'vint', 'trenta', 'quaranta', 'cinquanta', 'seixanta', 'setanta', 'vuitanta', 'noranta'];

function catalanBelow1000(n: number, beforeNoun = false): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (hundreds) parts.push(hundreds === 1 ? 'cent' : `${CA_UNITS[hundreds]}-cents`);
  if (rest || !hundreds) {
    const unit = (value: number) => (beforeNoun && value === 1 ? 'un' : CA_UNITS[value]!);
    if (rest < 20) parts.push(unit(rest));
    else {
      const tens = Math.floor(rest / 10);
      const ones = rest % 10;
      parts.push(ones ? `${CA_TENS[tens]}${tens === 2 ? '-i-' : '-'}${unit(ones)}` : CA_TENS[tens]!);
    }
  }
  return parts.join(' ');
}

function catalanNumber(n: number): string {
  if (n < 1000) return catalanBelow1000(n);
  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const parts: string[] = [];
  if (millions) parts.push(millions === 1 ? 'un milió' : `${catalanBelow1000(millions, true)} milions`);
  if (thousands) parts.push(thousands === 1 ? 'mil' : `${catalanBelow1000(thousands, true)} mil`);
  if (rest) parts.push(catalanBelow1000(rest));
  return parts.join(' ');
}

const NL_ONES = ['nul', 'een', 'twee', 'drie', 'vier', 'vijf', 'zes', 'zeven', 'acht', 'negen', 'tien', 'elf', 'twaalf', 'dertien', 'veertien', 'vijftien', 'zestien', 'zeventien', 'achttien', 'negentien'];
const NL_TENS = ['', '', 'twintig', 'dertig', 'veertig', 'vijftig', 'zestig', 'zeventig', 'tachtig', 'negentig'];

function dutchBelow100(n: number): string {
  if (n < 20) return NL_ONES[n]!;
  const ones = n % 10;
  const tens = NL_TENS[Math.floor(n / 10)]!;
  if (!ones) return tens;
  const unit = NL_ONES[ones]!;
  // "tweeëntwintig", "drieëntwintig": the joining "en" takes a trema after an e.
  return `${unit}${unit.endsWith('e') ? 'ën' : 'en'}${tens}`;
}

function dutchBelow1000(n: number): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const head = hundreds ? `${hundreds === 1 ? '' : NL_ONES[hundreds]}honderd` : '';
  return rest || !hundreds ? `${head}${dutchBelow100(rest)}` : head;
}

function dutchNumber(n: number): string {
  if (n < 1000) return dutchBelow1000(n);
  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const parts: string[] = [];
  if (millions) parts.push(`${millions === 1 ? 'een' : dutchBelow1000(millions)} miljoen`);
  if (thousands || rest) parts.push(`${thousands ? `${thousands === 1 ? '' : dutchBelow1000(thousands)}duizend` : ''}${rest ? dutchBelow1000(rest) : ''}`);
  return parts.join(' ');
}

/** 1960 → "negentienhonderdzestig" (years 1100–1999 are said in hundreds). */
function dutchYear(n: number): string {
  if (n >= 1100 && n < 2000 && n % 1000 >= 100) {
    const rest = n % 100;
    return `${dutchBelow100(Math.floor(n / 100))}honderd${rest ? dutchBelow100(rest) : ''}`;
  }
  return dutchNumber(n);
}

const NUMBER: Record<AiChatLanguage, (n: number) => string> = { en: englishNumber, es: spanishNumber, ca: catalanNumber, nl: dutchNumber };
const YEAR: Record<AiChatLanguage, (n: number) => string> = { en: englishYear, es: spanishNumber, ca: catalanNumber, nl: dutchYear };
const POINT: Record<AiChatLanguage, string> = { en: 'point', es: 'coma', ca: 'coma', nl: 'komma' };
const MINUS: Record<AiChatLanguage, string> = { en: 'minus', es: 'menos', ca: 'menys', nl: 'min' };
const PERCENT: Record<AiChatLanguage, string> = { en: 'percent', es: 'por ciento', ca: 'per cent', nl: 'procent' };
const DEGREES_C: Record<AiChatLanguage, string> = { en: 'degrees Celsius', es: 'grados centígrados', ca: 'graus centígrads', nl: 'graden Celsius' };
const KM: Record<AiChatLanguage, string> = { en: 'kilometres', es: 'kilómetros', ca: 'quilòmetres', nl: 'kilometer' };

/** A number in words, in the language; null outside 0 … 999 999 999. */
export function numberToWords(value: number, language: AiChatLanguage): string | null {
  if (!Number.isInteger(value) || value < 0 || value > 999_999_999) return null;
  return NUMBER[language](value);
}

// --- Whole replies ----------------------------------------------------------------

function stripMarkdown(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+[.)])\s+/gm, '')
    .replace(/(\*\*|__|\*|_)(\S[\s\S]*?\S|\S)\1/g, '$2')
    .replace(/[ \t]+/g, ' ')
    .trim();
}

/**
 * The reply as it should be spoken: markdown and links out; numbers, years,
 * decimals, percentages, °C and km in words, in the reply's language.
 */
export function toSpeakableText(text: string, language: AiChatLanguage): string {
  const say = NUMBER[language];
  const commaDecimals = language !== 'en';
  let out = stripMarkdown(text);

  // Thousands separators first ("10,000" / "10.000"), so they are read as one number.
  out = out.replace(commaDecimals ? /\b\d{1,3}(?:\.\d{3})+\b/g : /\b\d{1,3}(?:,\d{3})+\b/g, (match) => match.replace(/[.,]/g, ''));

  out = out.replace(/(-|−)?\b(\d+)(?:([.,])(\d+))?\b(\s*%|\s*°\s*C\b|\s*km\b)?/g, (match: string, ...groups: Array<string | undefined>) => {
    const [sign, whole = '', separator, fraction, unit] = groups;
    const integer = Number(whole);
    if (!Number.isSafeInteger(integer) || integer > 999_999_999) return match;
    // A decimal in this language's notation (either mark in English replies "9.5", "9,5" elsewhere too).
    const isDecimal = fraction !== undefined && (separator === '.' || commaDecimals);
    let words: string;
    if (isDecimal) {
      const digits = fraction!.length <= 2 && !fraction!.startsWith('0') ? say(Number(fraction)) : [...fraction!].map((digit) => say(Number(digit))).join(' ');
      words = `${say(integer)} ${POINT[language]} ${digits}`;
    } else if (fraction !== undefined) {
      return match; // "3,4" in English: a list, not a number; leave it
    } else {
      const looksLikeYear = !unit && !sign && whole.length === 4 && integer >= 1100 && integer <= 2099;
      words = looksLikeYear ? YEAR[language](integer) : say(integer);
    }
    const trimmedUnit = unit?.replace(/\s+/g, '');
    const unitWords = trimmedUnit === '%' ? ` ${PERCENT[language]}` : trimmedUnit === '°C' ? ` ${DEGREES_C[language]}` : trimmedUnit === 'km' ? ` ${KM[language]}` : '';
    return `${sign ? `${MINUS[language]} ` : ''}${words}${unitWords}`;
  });

  return out.replace(/\s+/g, ' ').trim();
}

/**
 * The reply cut into pieces a voice can say one after another: whole sentences,
 * none longer than `max` characters, so the first can play while the next is made.
 */
export function splitForSpeech(text: string, max = 280): string[] {
  const sentences = text.match(/[^.!?…]+(?:[.!?…]+["»”')\]]*|$)/g)?.map((part) => part.trim()).filter(Boolean) ?? [];
  const out: string[] = [];
  for (const sentence of sentences) {
    if (sentence.length <= max) {
      out.push(sentence);
      continue;
    }
    // A very long sentence: break at commas, then at spaces.
    let rest = sentence;
    while (rest.length > max) {
      const window = rest.slice(0, max);
      const at = Math.max(window.lastIndexOf(', '), window.lastIndexOf('; '));
      const cut = at > max / 3 ? at + 1 : window.lastIndexOf(' ') > 0 ? window.lastIndexOf(' ') : max;
      out.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest) out.push(rest);
  }
  return out;
}
